import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { EnterpriseMember } from 'models/Enterprises';
import {
  getArrayLength,
  getUserFullNameOrEmail,
  renderFullUserInfo,
} from 'utils';
import useInvitations from '../hooks/useInvitations';

import './SendInvitatesOrRemindersModal.styles.scss';

const MAX_NUMBER_TO_SHOW = 10;

type SendInvitatesOrRemindersModalProps = {
  className?: string;
  enterpriseId: string;
  updateMembers: (members: EnterpriseMember[]) => void;
} & Omit<ModalProps, 'title'>;

export type SendInvitatesOrRemindersModalRef = {
  open: (members: EnterpriseMember[], isReminder: boolean) => void;
};

const SendInvitatesOrRemindersModal: React.ForwardRefRenderFunction<
  SendInvitatesOrRemindersModalRef,
  SendInvitatesOrRemindersModalProps
> = (props, ref) => {
  const { className, enterpriseId, updateMembers, ...rest } = props;

  const [isReminder, setIsReminder] = useState(false);
  const [members, setMembers] = useState<EnterpriseMember[]>([]);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const { onSendInvites, onSendReminders } = useInvitations(
    enterpriseId,
    updateMembers,
  );

  const { t } = useTranslation();

  const count = useMemo(() => getArrayLength(members), [members]);
  const isSingle = useMemo(() => count === 1, [count]);

  const hasMore = useMemo(() => count > MAX_NUMBER_TO_SHOW, [count]);

  const title = useMemo(
    () =>
      t(isReminder ? 'confirmReminders' : 'confirmInvitations', {
        count,
      }),
    [count, isReminder, t],
  );

  const description = useMemo(
    () =>
      t(isReminder ? 'confirmRemindersBody' : 'confirmInvitationsBody', {
        count,
        memberName: getUserFullNameOrEmail(members[0]),
      }),
    [count, isReminder, members, t],
  );

  const ctaContent = useMemo(
    () =>
      t(isReminder ? 'sendReminders' : 'sendInvites', {
        count,
      }),
    [count, isReminder, t],
  );

  const ctaAction = useCallback(() => {
    isReminder ? onSendReminders(members) : onSendInvites(members);
    modal?.close();
  }, [isReminder, members, modal, onSendInvites, onSendReminders]);

  const open = useCallback(
    (members: EnterpriseMember[], isReminder: boolean) => {
      setMembers(members);
      setIsReminder(isReminder);
      modal?.open();
    },
    [modal],
  );

  useImperativeHandle(ref, () => ({ open }), [open]);

  const classes = classNames('send-invitates-or-reminders-modal', className);

  return (
    <Modal ref={modalRef} className={classes} title={title} {...rest}>
      <div>
        <p>{description}</p>

        {!isSingle && (
          <>
            <ul>
              {members.slice(0, MAX_NUMBER_TO_SHOW).map((member) => (
                <li key={member._id}>{renderFullUserInfo(member)}</li>
              ))}
            </ul>

            {hasMore && (
              <p>{t('andMore', { count: count - MAX_NUMBER_TO_SHOW })}</p>
            )}
          </>
        )}

        <div className="actions">
          <Button variant="link" onClick={modal?.close}>
            {t('cancel')}
          </Button>
          <Button onClick={ctaAction}>{ctaContent}</Button>
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(SendInvitatesOrRemindersModal);
